import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import BlogArticleMarkdown from "./BlogArticleMarkdown.mjs";
import { Helmet } from "react-helmet-async";
import { formatDateOnly } from "../../utils/date";
import "./Blog.css";

import { SITE_URL, PUBLIC_ROBOTS, blogCoverAttributes, blogStructuredData } from "../../utils/siteSeo.mjs";
const IOS_URL = "https://apps.apple.com/us/app/range-pocket/id6733222657";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket";
const BODY_HEADINGS = { h1: ({ children }) => <h2>{children}</h2> };

const RELATED_LINKS = [
  { href: "/range-pocket", label: "Explore Range Pocket" },
  { href: "/features", label: "Range Pocket features" },
  { href: "/faq", label: "Read the Range Pocket FAQ" },
  { href: "/firearm-inventory-app", label: "Firearm inventory app" },
  { href: "/range-log-app", label: "Range log app" },
  { href: "/blog/range-pocket-records", label: "How records work" },
  { href: "/blog/range-time-to-training-habit", label: "Build a training habit" },
];

const ARTICLE_CTA_BY_SLUG = {
  "apex-florida-state-precision-pistol-2026": {
    eyebrow: "From the championship",
    title: "Explore the Florida State match results.",
    body: "Follow the aggregate standings and open the course-by-course score breakdowns from Gateway Rifle & Pistol Club.",
    links: [
      { href: "/matches/bullseye/dd49e852-f6a4-4fc3-a05a-f4e894066394", label: "Match overview" },
      { href: "/matches/bullseye/dd49e852-f6a4-4fc3-a05a-f4e894066394/results", label: "Full results" },
    ],
    downloadTitle: "Bring Range Pocket to your next match.",
    relatedLinks: [
      { href: "/matches", label: "Find matches" },
      { href: "/range-pocket", label: "Explore Range Pocket" },
      { href: "/features", label: "Beyond competition" },
      { href: "/contact", label: "Contact ArmorySync" },
    ],
  },
  "firearm-maintenance-log-what-to-track": {
    eyebrow: "Maintenance workflow",
    title: "Keep round counts, cleaning notes, and reminders connected to each firearm.",
    body:
      "Range Pocket keeps maintenance context beside the firearm record, range history, and accessories it belongs to.",
    image: "/content/blog/firearm-maintenance-log-what-to-track/maintenance_plan_screen.png",
    links: [
      { href: "/features/firearm-maintenance", label: "Maintenance feature" },
      { href: "/firearm-maintenance-tracker", label: "Maintenance tracker" },
      { href: "/range-pocket", label: "Open Range Pocket" },
    ],
  },
  "what-to-record-after-range-visit": {
    eyebrow: "Range log workflow",
    title: "Turn each range trip into a record you can actually review later.",
    body:
      "Link firearms, round counts, target photos, notes, drills, and saved ranges in one session history.",
    image: "/features/feature-range-visits.webp",
    links: [
      { href: "/features/range-visit-logging", label: "Range logging feature" },
      { href: "/range-log-app", label: "Range log app" },
      { href: "/features/training-plans", label: "Training plans" },
    ],
  },
  "private-firearm-inventory-without-spreadsheet": {
    eyebrow: "Inventory workflow",
    title: "Replace spreadsheet rows with firearm records that connect to real use.",
    body:
      "Track photos, purchase details, accessories, maintenance, and range history without keeping everything in separate notes.",
    image: "/features/feature-firearm-list.webp",
    links: [
      { href: "/features/firearm-inventory", label: "Inventory feature" },
      { href: "/firearm-inventory-app", label: "Inventory app page" },
      { href: "/features/accessory-tracking", label: "Accessory tracking" },
    ],
  },
  "range-time-to-training-habit": {
    eyebrow: "Training workflow",
    title: "Make the next session easier to plan before you get to the range.",
    body:
      "Use drills, training plans, visit notes, and target photos to build continuity between sessions.",
    image: "/features/feature-training-overview.webp",
    links: [
      { href: "/features/training-plans", label: "Training plans" },
      { href: "/range-log-app", label: "Range log app" },
      { href: "/range-pocket", label: "Open Range Pocket" },
    ],
  },
  "building-better-training-habits": {
    eyebrow: "Training workflow",
    title: "Keep drills, notes, and range history close to the next session.",
    body:
      "Range Pocket helps owners turn range time into a repeatable training habit with clearer review between sessions.",
    image: "/features/feature-training-overview.webp",
    links: [
      { href: "/features/training-plans", label: "Training features" },
      { href: "/range-pocket", label: "Range Pocket" },
      { href: "/blog/range-time-to-training-habit", label: "Training habit guide" },
    ],
  },
  "range-pocket-records": {
    eyebrow: "Product basics",
    title: "See how Range Pocket organizes inventory, range logs, and training in one app.",
    body:
      "The product pages show real app screens and the everyday workflows Range Pocket is built around.",
    image: "/features/feature-armory-status.webp",
    links: [
      { href: "/features", label: "Feature overview" },
      { href: "/range-pocket", label: "Range Pocket" },
      { href: "/faq", label: "FAQ" },
    ],
  },
  "range-pocket-complete-tool": {
    eyebrow: "Product overview",
    title: "Explore the Range Pocket features behind the everyday workflow.",
    body:
      "Inventory, range visits, maintenance, training, ammo, accessories, and photos stay connected across the app.",
    image: "/features/feature-armory-status.webp",
    links: [
      { href: "/features", label: "All Range Pocket features" },
      { href: "/firearm-inventory-app", label: "Inventory app" },
      { href: "/range-log-app", label: "Range log app" },
    ],
  },
  "welcome-armorysync": {
    eyebrow: "Start here",
    title: "Start with Range Pocket, the ArmorySync product available today.",
    body:
      "The app helps owners organize inventory, range visits, maintenance, training notes, and useful history.",
    image: "/features/feature-armory-status.webp",
    links: [
      { href: "/range-pocket", label: "Explore Range Pocket" },
      { href: "/features", label: "View features" },
      { href: "/about", label: "About ArmorySync" },
    ],
  },
};

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [body, setBody] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setErr(null);
      setPost(null);
      setBody("");
      try {
        const metaRes = await fetch("/content/blog/posts.json", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!metaRes.ok)
          throw new Error(`Failed to fetch posts.json: ${metaRes.status}`);
        const meta = await metaRes.json();
        const match = meta.find((m) => m.slug === slug);
        if (!match) throw Object.assign(new Error("Post not found"), { status: 404 });

        const mdRes = await fetch(`/content/blog/${slug}/index.md`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!mdRes.ok)
          throw Object.assign(new Error("Unable to load story"), { status: mdRes.status });
        const text = await mdRes.text();
        if (controller.signal.aborted) return;
        setPost(match);
        setBody(text);
      } catch (e) {
        if (!controller.signal.aborted) setErr(e.status === 404 ? "missing" : "unavailable");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [slug]);

  const dateStr = useMemo(
    () => (post ? formatDateOnly(post.date) : ""),
    [post]
  );

  if (loading)
    return (
      <div className="blog-detail">
        <p>Loading…</p>
      </div>
    );
  if (err)
    return (
      <div className="blog-detail">
        <Helmet>
          <title>{err === "missing" ? "Story not found" : "Story unavailable"} | ArmorySync</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <h1>{err === "missing" ? "Story not found" : "Story unavailable"}</h1>
        <p>{err === "missing" ? "This story may have moved or is no longer available." : "We couldn't load this story. Please try again shortly."}</p>
        <Link className="blog-breadcrumb" to="/blog">Back to all stories</Link>
      </div>
    );
  if (!post) return null;
  const metaDescription =
    post.subtitle ||
    "ArmorySync builds practical firearm management apps like Range Pocket.";

  const postUrl = `${SITE_URL}/blog/${post.slug}`;
  const ogImage = post.cover
    ? `${SITE_URL}${post.cover.startsWith("/") ? post.cover : `/${post.cover}`}`
    : `${SITE_URL}/features/feature-armory-status.webp`;
  const articleCta = ARTICLE_CTA_BY_SLUG[post.slug] || ARTICLE_CTA_BY_SLUG["range-pocket-complete-tool"];

  return (
    <article className={`blog-detail${post.layout === "field-report" ? " blog-field-report" : ""}`}>
      <Helmet>
        <title>{post.title} • ArmorySync</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content={PUBLIC_ROBOTS} />
        <link rel="canonical" href={postUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={post.coverAlt || post.title} />
        <meta property="og:url" content={postUrl} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.updatedAt || post.date} />
        {(post.tags || []).map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify(blogStructuredData(post))}
        </script>
      </Helmet>

      <Link className="blog-breadcrumb" to="/blog">
        ArmorySync Blog
      </Link>

      <header className="blog-article-header">
        <h1>{post.title}</h1>

        {post.subtitle && <p className="subtitle">{post.subtitle}</p>}

        <div className="blog-article-meta">
          <time dateTime={post.date}>{dateStr}</time>
          {post.updatedAt && post.updatedAt !== post.date && (
            <span>Updated {formatDateOnly(post.updatedAt)}</span>
          )}
        </div>

        {post.tags?.length > 0 && (
          <div className="blog-tags blog-tags-header">
            {post.tags.map((t) => (
              <span className="blog-tag" key={t}>
                {t}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Hero image */}
      {post.cover && (
        <figure className="cover">
          <img {...blogCoverAttributes(post)} alt={post.coverAlt || post.title} loading="eager" />
          {post.coverCaption && <figcaption>{post.coverCaption}</figcaption>}
        </figure>
      )}

      {/* Body */}
      <div className="blog-content">
        {post.layout === "field-report"
          ? <BlogArticleMarkdown body={body} media={post.media} />
          : <ReactMarkdown components={BODY_HEADINGS}>{body}</ReactMarkdown>}
      </div>

      <aside className="blog-context-cta" aria-label="Related Range Pocket workflow">
        <div className="blog-context-copy">
          <span>{articleCta.eyebrow}</span>
          <h2>{articleCta.title}</h2>
          <p>{articleCta.body}</p>
          <div className="blog-context-links">
            {articleCta.links.map((link) => (
              <Link to={link.href} key={link.href}>{link.label}</Link>
            ))}
          </div>
        </div>
        {articleCta.image && <img src={articleCta.image} alt="" loading="lazy" />}
      </aside>

      <aside className="blog-download-cta" aria-label="Try Range Pocket">
        <div>
          <span>Range Pocket</span>
          <h2>{articleCta.downloadTitle || "Try the firearm inventory and range log app."}</h2>
          <p className="blog-support-note">
            Questions before downloading? Email <a href="mailto:support@armorysync.com">support@armorysync.com</a>.
          </p>
        </div>
        <div className="blog-download-actions">
          <a href={IOS_URL}>App Store</a>
          <a href={ANDROID_URL}>Google Play</a>
          {!articleCta.downloadTitle && <Link to="/blog/range-pocket-records">How records work</Link>}
        </div>
      </aside>

      <aside className="blog-related" aria-label="Related ArmorySync resources">
        <h2>Related resources</h2>
        <div className="blog-related-links">
          {(articleCta.relatedLinks || RELATED_LINKS).filter((link) => link.href !== `/blog/${post.slug}`).map((link) => (
            <Link to={link.href} key={link.href}>{link.label}</Link>
          ))}
        </div>
      </aside>
    </article>
  );
}

export const BlogDetail_Route = {
  path: "/blog/:slug",
  element: <BlogDetail />,
};
